import React from "react";
import { Paper, Stack, Typography, Box, Skeleton } from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "./CMStatCards";
import UnmanagedRiskTable from "./UnmanagedRiskTable";

/**
 * Panel 2: Unmanaged High Risk — pacientes high-risk sin Care Manager.
 * Muestra summary stats (At Risk, Complex, End of Life) + tabla detallada.
 *
 * @param {{ data: Object, loading: boolean, onCreateTask: Function, helpContent: Object }} props
 */
export default function PanelUnmanagedRisk({ data, loading, onCreateTask, helpContent }) {
  // ─── Loading State ───────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} animation="wave" sx={{ borderRadius: 2 }} />
        </Stack>
      </Paper>
    );
  }

  const hasPatients = data?.patients?.length > 0;

  // ─── Stat Card Items ─────────────────────────────────────────
  const statItems = [
    {
      label: "At Risk",
      value: data?.summary?.atRiskNoCM ?? 0,
      color: "#D97706",
      bgColor: "#FEF3C7",
    },
    {
      label: "Complex",
      value: data?.summary?.complexNoCM ?? 0,
      color: "#DC2626",
      bgColor: "#FEE2E2",
    },
    {
      label: "End of Life",
      value: data?.summary?.eolNoCM ?? 0,
      color: "#7C3AED",
      bgColor: "#F3E8FF",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: "#FEE2E2",
              color: "#DC2626",
            }}
          >
            <ReportProblemIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Unmanaged High Risk
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Summary Stat Cards */}
        <CMStatCards items={statItems} />

        {/* Table or Empty State */}
        {hasPatients ? (
          <UnmanagedRiskTable patients={data.patients} onCreateTask={onCreateTask} />
        ) : (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "#DCFCE7",
              textAlign: "center",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
              <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: "#166534" }}>
                All high-risk patients are managed
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
